import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

/**
 * 归一化工具栏命令列表。
 *
 * 设计说明：
 * 1. 工具栏激活态只关心命令标识，不应该保留空字符串；
 * 2. 使用 Set 去重，避免同一命令被重复激活后导致 UI 判断出现不必要的重复扫描；
 * 3. 返回新数组，确保依赖 activeCommands 的组件可以稳定收到响应式更新。
 *
 * @param commands 待写入的命令列表。
 * @returns 去空、去重后的命令列表。
 */
function normalizeToolbarCommands(commands: readonly string[]): string[] {
  return Array.from(new Set(commands.filter((command) => command.length > 0)));
}

/**
 * 工具栏命令激活态 Store。
 *
 * 职责边界：
 * 1. 只保存顶部工具栏按钮的激活/高亮命令列表；
 * 2. 不执行 CAD 命令，不记录历史，不触碰 Viewer；
 * 3. 各 workflow 负责写入命令态，各 Toolbar 组件负责读取命令态。
 */
export const useToolbarCommandStore = defineStore('toolbar-command', () => {
  /** 当前激活的工具栏命令列表。 */
  const activeCommands = ref<string[]>([]);

  /** 当前是否存在任意激活命令。 */
  const hasActiveCommand = computed(() => activeCommands.value.length > 0);

  /**
   * 用完整列表替换当前激活命令。
   *
   * @param commands 新的激活命令列表。
   */
  function setActiveCommands(commands: readonly string[]): void {
    activeCommands.value = normalizeToolbarCommands(commands);
  }

  /**
   * 激活单个命令。
   *
   * 说明：该方法用于需要叠加高亮的入口；互斥命令应优先调用 `setActiveCommands`。
   *
   * @param command 需要激活的命令。
   */
  function activateCommand(command: string): void {
    setActiveCommands([...activeCommands.value, command]);
  }

  /**
   * 取消指定命令；未传入命令时清空所有激活命令。
   *
   * @param command 需要取消的命令。
   */
  function deactivateCommand(command?: string): void {
    if (!command) {
      clearActiveCommands();
      return;
    }
    activeCommands.value = activeCommands.value.filter((item) => item !== command);
  }

  /**
   * 清空所有工具栏激活命令。
   */
  function clearActiveCommands(): void {
    activeCommands.value = [];
  }

  /**
   * 判断命令是否处于激活态。
   *
   * @param command 待检查的命令。
   * @returns 命令已激活时返回 true。
   */
  function isCommandActive(command?: string): boolean {
    return Boolean(command && activeCommands.value.includes(command));
  }

  return {
    activeCommands,
    hasActiveCommand,
    setActiveCommands,
    activateCommand,
    deactivateCommand,
    clearActiveCommands,
    isCommandActive,
  };
});
